import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { announcementApi } from '../../services/announcementApi';
import { Bell, Calendar, Pin, AlertCircle, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AnnouncementsWidget = () => {
    const { isAuthenticated } = useAuth();
    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            if (!isAuthenticated) return;
            try {
                setIsLoading(true);
                const data = await announcementApi.getDashboardAnnouncements();
                setAnnouncements(data || []);
            } catch (err) {
                console.error('Duyurular yüklenemedi', err);
                setError('Duyurular yüklenemedi');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAnnouncements();
    }, [isAuthenticated]);

    if (isLoading) {
        return (
            <div className="flex min-h-[260px] items-center justify-center rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500/20 border-t-indigo-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-slate-200/80 bg-white p-6 text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <AlertCircle className="mb-2 h-8 w-8 text-rose-400" />
                <p className="text-xs font-semibold">{error}</p>
            </div>
        );
    }

    if (announcements.length === 0) {
        return null;
    }

    return (
        <>
            <div className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="flex items-center text-base font-bold text-slate-900 dark:text-white">
                        <Bell className="mr-2 h-5 w-5 text-indigo-500" />
                        Şirket İçi Duyurular
                    </h3>
                    <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-[11px] font-bold text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300">
                        {announcements.length} Aktif
                    </span>
                </div>

                <div className="space-y-2.5">
                    {announcements.map((announcement) => (
                        <motion.div
                            key={announcement.id}
                            whileHover={{ scale: 1.005 }}
                            whileTap={{ scale: 0.995 }}
                            onClick={() => setSelectedAnnouncement(announcement)}
                            className={`cursor-pointer rounded-xl border px-4 py-3 transition-all ${
                                announcement.pinned
                                    ? 'border-amber-200/90 bg-gradient-to-r from-amber-50/70 to-white dark:border-amber-800/50 dark:from-amber-950/30 dark:to-slate-900'
                                    : 'border-slate-200/80 bg-slate-50/70 hover:border-slate-300 hover:bg-white dark:border-slate-800 dark:bg-slate-800/50 dark:hover:border-slate-700'
                            }`}
                        >
                            <div className="mb-1 flex items-center justify-between gap-2">
                                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                                    {announcement.pinned && (
                                        <Pin className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                                    )}
                                    <h4 className="truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                                        {announcement.title}
                                    </h4>
                                </div>
                                <div className="flex shrink-0 items-center text-[10px] font-semibold text-slate-400 dark:text-slate-500">
                                    <Calendar className="mr-1 h-3 w-3" />
                                    {new Date(announcement.publishStartAt || announcement.createdAt).toLocaleDateString('tr-TR', {
                                        day: 'numeric',
                                        month: 'short'
                                    })}
                                </div>
                            </div>
                            <p className="line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
                                {announcement.summary || announcement.content}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {selectedAnnouncement && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.96 }}
                            className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
                        >
                            <div
                                className={`border-b px-6 py-4 ${
                                    selectedAnnouncement.pinned
                                        ? 'border-amber-200/80 bg-amber-50/60 dark:border-slate-800 dark:bg-slate-800/80'
                                        : 'border-slate-200 bg-slate-50/70 dark:border-slate-800 dark:bg-slate-800/60'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                        {selectedAnnouncement.title}
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedAnnouncement(null)}
                                        className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-200/60 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                                <div className="mt-1.5 flex items-center text-xs font-medium text-slate-500 dark:text-slate-400">
                                    <Calendar className="mr-1.5 h-3.5 w-3.5" />
                                    {new Date(selectedAnnouncement.publishStartAt || selectedAnnouncement.createdAt).toLocaleDateString('tr-TR', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </div>
                            </div>

                            <div className="overflow-y-auto p-6">
                                <AnnouncementDetailContent announcement={selectedAnnouncement} />
                            </div>

                            <div className="flex justify-end gap-2.5 border-t border-slate-200 bg-slate-50/70 px-6 py-3.5 dark:border-slate-800 dark:bg-slate-900/60">
                                {selectedAnnouncement.actionUrl && selectedAnnouncement.actionButtonText && (
                                    <a
                                        href={selectedAnnouncement.actionUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-indigo-700"
                                    >
                                        {selectedAnnouncement.actionButtonText}
                                        <ChevronRight className="ml-1 h-4 w-4" />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setSelectedAnnouncement(null)}
                                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                                >
                                    Kapat
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};

const AnnouncementDetailContent = ({ announcement }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(!announcement?.content);

    useEffect(() => {
        if (announcement?.content) {
            setDetail(announcement);
            setLoading(false);
            return;
        }
        let isMounted = true;
        const fetchDetail = async () => {
            try {
                const data = await announcementApi.getById(announcement.id);
                if (isMounted) setDetail(data);
            } catch (err) {
                console.error('Detay yüklenemedi', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchDetail();
        return () => {
            isMounted = false;
        };
    }, [announcement]);

    if (loading) {
        return <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />;
    }

    const fullContent = detail?.content || announcement?.summary || '';
    const summaryText = (announcement?.summary || '').trim();
    const isAutoTruncatedSummary =
        !summaryText ||
        summaryText === fullContent.trim() ||
        (summaryText.endsWith('...') && fullContent.startsWith(summaryText.slice(0, -3)));

    return (
        <div className="space-y-3">
            {!isAutoTruncatedSummary && (
                <div className="rounded-xl border border-slate-200/80 bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
                    {summaryText}
                </div>
            )}
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                {fullContent}
            </p>
        </div>
    );
};

export default AnnouncementsWidget;
